<template>
  <div class="as-employee-checklist-inspection">
    <div class="as-employee-checklist-inspection__toolbar">
      <div
        class="as-employee-checklist-inspection__filter"
        role="radiogroup"
        aria-label="Checkpoint filter"
      >
        <button
          v-for="option in filterOptions"
          :key="option.value"
          type="button"
          class="as-employee-checklist-inspection__filter-option"
          :class="{
            'as-employee-checklist-inspection__filter-option--active':
              resolvedPassFilter === option.value,
          }"
          role="radio"
          :aria-checked="String(resolvedPassFilter === option.value)"
          @click="onPassFilterChange(option.value)"
        >
          {{ option.label }}
        </button>
      </div>

      <div class="as-employee-checklist-inspection__toolbar-end">
        <div class="as-employee-checklist-inspection__toolbar-extra">
          <slot name="toolbar-extra" />
        </div>
        <div
          class="as-employee-checklist-inspection__search"
          role="search"
          aria-label="Search Criteria"
        >
          <ExTextInput
            :value="resolvedSearchQuery"
            ghost-text="Search Criteria"
            icon="interface_search"
            label=""
            hide-floating-label
            custom-height="40px"
            @input="onSearchInput"
          />
        </div>
      </div>
    </div>

    <div class="as-employee-checklist-inspection__groups">
      <section
        v-for="row in displayLevels"
        :key="row.key"
        class="as-employee-checklist-inspection__group"
      >
        <button
          type="button"
          class="as-employee-checklist-inspection__group-header"
          :class="{
            'as-employee-checklist-inspection__group-header--static': !row.showChevron,
          }"
          :aria-expanded="row.showChevron ? String(row.expanded) : undefined"
          :aria-label="`${row.level} checkpoints`"
          @click="onToggleLevel(row)"
        >
          <span
            class="as-employee-checklist-inspection__chevron"
            :class="{
              'as-employee-checklist-inspection__chevron--hidden': !row.showChevron,
            }"
            aria-hidden="true"
          >
            {{ row.expanded ? "▼" : "▶" }}
          </span>

          <InspectionLevelBadge :level="row.level" />

          <span class="as-employee-checklist-inspection__summary">
            {{ row.summary }}
          </span>

          <span
            v-if="row.showProgress"
            class="as-employee-checklist-inspection__progress"
            role="progressbar"
            :aria-valuenow="row.progressPercent"
            aria-valuemin="0"
            aria-valuemax="100"
            :aria-label="`${row.level} progress`"
          >
            <span
              class="as-employee-checklist-inspection__progress-fill"
              :class="row.progressFillClass"
              :style="{ '--progress-pct': String(row.progressPercent) }"
            />
          </span>
        </button>

        <ul
          v-if="row.expanded && row.hasCheckpoints"
          class="as-employee-checklist-inspection__cards"
        >
          <li
            v-for="checkpoint in row.checkpoints"
            :key="checkpointKey(checkpoint)"
            class="as-employee-checklist-inspection__card-item"
          >
            <AsEmployeeCheckpointInspectionCard
              :type="checkpoint.type"
              :status="checkpoint.status"
              :title="checkpoint.title"
              :action-label="checkpoint.actionLabel || ''"
              :secondary-action-label="checkpoint.secondaryActionLabel || ''"
              :inspection-id="checkpoint.id"
              @action-click="onCheckpointAction(row.group, checkpoint, $event)"
              @secondary-action-click="onCheckpointSecondaryAction(row.group, checkpoint, $event)"
              @chatter-click="onCheckpointChatter(row.group, checkpoint)"
            />
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import ExTextInput from "@components/TextInput/ExTextInput.vue";
import AsEmployeeCheckpointInspectionCard from "@components/AsEmployeeCheckpointInspectionCard/AsEmployeeCheckpointInspectionCard.vue";
import InspectionLevelBadge from "@components/InspectionLevelBadge/InspectionLevelBadge.vue";

const filterOptions = [
  { value: "not-yet-passed", label: "Not yet passed" },
  { value: "all", label: "All" },
];

const props = defineProps({
  /**
   * Level groups for the accordion.
   * Shape: `{ id?, level, passed, total, expanded, emptyLabel?, checkpoints[] }`
   * Checkpoint shape matches AsEmployeeCheckpointInspectionCard props (+ optional `id`).
   */
  levels: {
    type: Array,
    default: () => [],
    validator: (groups) =>
      groups.every(
        (group) =>
          group &&
          typeof group.level === "string" &&
          ["Apprentice", "Professional", "Coach", "Master"].includes(group.level)
      ),
  },
  /** Active pass filter: `not-yet-passed` | `all`. */
  passFilter: {
    type: String,
    default: "not-yet-passed",
    validator: (value) => ["not-yet-passed", "all"].includes(value),
  },
  /** Controlled search query. */
  searchQuery: {
    type: String,
    default: "",
  },
});

const emit = defineEmits([
  "update:passFilter",
  "update:searchQuery",
  "pass-filter-change",
  "search",
  "toggle-level",
  "action-click",
  "secondary-action-click",
  "chatter-click",
]);

const resolvedPassFilter = computed(() => props.passFilter || "not-yet-passed");
const resolvedSearchQuery = computed(() => props.searchQuery || "");

function checkpointKey(checkpoint) {
  return checkpoint.id || `${checkpoint.type}-${checkpoint.title}-${checkpoint.status}`;
}

function groupSummary(group) {
  const total = Number(group.total) || 0;
  if (total <= 0) {
    return group.emptyLabel || "No checkpoints required";
  }
  const passed = Number(group.passed) || 0;
  return `${passed} of ${total} checkpoints passed`;
}

function progressPercentFor(group) {
  const total = Number(group.total) || 0;
  if (total <= 0) return 0;
  const passed = Number(group.passed) || 0;
  return Math.max(0, Math.min(100, Math.round((passed / total) * 100)));
}

function progressFillClassFor(group) {
  const total = Number(group.total) || 0;
  const passed = Number(group.passed) || 0;
  if (total > 0 && passed >= total) {
    return "as-employee-checklist-inspection__progress-fill--complete";
  }
  return "as-employee-checklist-inspection__progress-fill--in-progress";
}

const displayLevels = computed(() =>
  (props.levels || []).map((group) => {
    const total = Number(group.total) || 0;
    const checkpoints = Array.isArray(group.checkpoints) ? group.checkpoints : [];
    const hasCheckpoints = checkpoints.length > 0;
    return {
      group,
      key: group.id || group.level,
      level: group.level,
      expanded: hasCheckpoints && !!group.expanded,
      summary: groupSummary(group),
      showProgress: total > 0,
      progressPercent: progressPercentFor(group),
      progressFillClass: progressFillClassFor(group),
      hasCheckpoints,
      showChevron: hasCheckpoints,
      checkpoints,
    };
  })
);

function onPassFilterChange(value) {
  if (value === resolvedPassFilter.value) return;
  emit("update:passFilter", value);
  emit("pass-filter-change", value);
}

function onSearchInput(value) {
  emit("update:searchQuery", value);
  emit("search", value);
}

function onToggleLevel(row) {
  if (!row.showChevron) return;
  emit("toggle-level", {
    id: row.group.id,
    level: row.group.level,
    expanded: !row.group.expanded,
  });
}

function onCheckpointAction(group, checkpoint, payload) {
  emit("action-click", { group, checkpoint, ...payload });
}

function onCheckpointSecondaryAction(group, checkpoint, payload) {
  emit("secondary-action-click", { group, checkpoint, ...payload });
}

function onCheckpointChatter(group, checkpoint) {
  emit("chatter-click", { group, checkpoint });
}
</script>

<style scoped>
.as-employee-checklist-inspection {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 14px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 20px;
  background: #ffffff;
  border-radius: 12px;
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
}

.as-employee-checklist-inspection__toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: flex-start;
  width: 100%;
}

.as-employee-checklist-inspection__filter {
  display: inline-flex;
  flex-shrink: 0;
  gap: 4px;
  padding: 3px;
  background: #f2f4f7;
  border-radius: 9999px;
}

.as-employee-checklist-inspection__filter-option {
  appearance: none;
  border: 1px solid transparent;
  background: transparent;
  padding: 5px 14px;
  border-radius: 9999px;
  color: #667085;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: normal;
  white-space: nowrap;
  cursor: pointer;
}

.as-employee-checklist-inspection__filter-option--active {
  background: #ffffff;
  border-color: #eaecf0;
  color: #101828;
}

.as-employee-checklist-inspection__filter-option:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.as-employee-checklist-inspection__toolbar-end {
  display: flex;
  flex: 1 1 0;
  gap: 16px;
  align-items: center;
  min-width: 0;
}

.as-employee-checklist-inspection__toolbar-extra {
  flex: 1 1 0;
  min-width: 0;
}

.as-employee-checklist-inspection__search {
  flex: 1 1 0;
  max-width: 320px;
  min-width: 0;
}

.as-employee-checklist-inspection__search :deep(.ex_text_input) {
  width: 100%;
}

.as-employee-checklist-inspection__search :deep(.main_label) {
  display: none;
}

.as-employee-checklist-inspection__search :deep(.v-text-field__details) {
  display: none;
}

.as-employee-checklist-inspection__groups {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  align-self: stretch;
  width: 100%;
  min-width: 0;
}

.as-employee-checklist-inspection__group {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  align-self: stretch;
  width: 100%;
  min-width: 0;
}

.as-employee-checklist-inspection__group-header {
  display: flex;
  gap: 10px;
  align-items: center;
  align-self: stretch;
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
  background: #ffffff;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  box-sizing: border-box;
}

.as-employee-checklist-inspection__group-header--static {
  cursor: default;
}

.as-employee-checklist-inspection__group-header:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
  border-radius: 4px;
}

.as-employee-checklist-inspection__chevron {
  flex-shrink: 0;
  color: #667085;
  font-size: 11px;
  font-weight: 600;
  line-height: normal;
}

.as-employee-checklist-inspection__chevron--hidden {
  visibility: hidden;
}

.as-employee-checklist-inspection__summary {
  flex-shrink: 0;
  color: #475467;
  font-size: 13px;
  font-weight: 400;
  line-height: normal;
  white-space: nowrap;
}

.as-employee-checklist-inspection__progress {
  position: relative;
  flex: 1 1 0;
  min-width: 48px;
  height: 6px;
  overflow: hidden;
  background: #eaecf0;
  border-radius: 9999px;
}

.as-employee-checklist-inspection__progress-fill {
  position: absolute;
  top: 0;
  left: 0;
  width: calc(var(--progress-pct, 0) * 1%);
  height: 6px;
  border-radius: 9999px;
}

.as-employee-checklist-inspection__progress-fill--in-progress {
  background: #7f56d9;
}

.as-employee-checklist-inspection__progress-fill--complete {
  background: #17b26a;
}

.as-employee-checklist-inspection__cards {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  align-self: stretch;
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

.as-employee-checklist-inspection__card-item {
  display: flex;
  align-self: stretch;
  width: 100%;
  min-width: 0;
}

.as-employee-checklist-inspection__card-item :deep(.as-employee-checkpoint-inspection-card) {
  flex: 1 1 auto;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}
</style>
